import {
  anonymize as anonymizeFullStory,
  identify as identifyFullStory,
} from '@fullstory/browser';

import type { User } from '@/types';

import { momentsAnalytics } from '@/services/momentsAnalytics';

const identifiedEmailsThisDocument = new Set<string>();
const postAuthenticationAuthEvents = new Set([
  'auth_discovery_callback_recovered_existing_session',
  'auth_login_success',
  'auth_mfa_sms_verify_success',
  'auth_mfa_totp_verify_success',
  'auth_redirect',
]);

const isDev = (): boolean => import.meta.env.DEV;
const identityKey = (email: string): string => email.trim();
const isPreAuthenticationEvent = (eventName: string): boolean =>
  eventName.startsWith('auth_') && !postAuthenticationAuthEvents.has(eventName);

export const getFullstoryOrgId = (): string | undefined =>
  import.meta.env.VITE_FULLSTORY_ORG;

// HubSpot and Fullstory remain browser integrations. Mixpanel identity is resolved
// by Moments for every event, so this file deliberately has no client identity state.
export const identify = (user: User): void => {
  const { email, fullName } = user;
  const key = identityKey(email);
  if (key) identifiedEmailsThisDocument.add(key);

  try {
    window._hsq = window._hsq || []; // eslint-disable-line no-underscore-dangle -- Hubspot naming rules
    const { _hsq } = window; // eslint-disable-line @typescript-eslint/naming-convention -- Hubspot naming rules
    _hsq.push(['identify', { email }]);
  } catch {
    // HubSpot is best effort.
  }

  try {
    if (getFullstoryOrgId()) {
      identifyFullStory(email, { displayName: fullName, email });
    } else if (isDev()) {
      // eslint-disable-next-line no-console -- dev-only gated debug logging
      console.debug(`FULLSTORY USER VARS ${email}`, {
        displayName: fullName,
        email,
      });
    }
  } catch {
    // FullStory is best effort.
  }
};

export const identifyEmail = (email: string): void => {
  const trimmed = email.trim();
  if (!trimmed) return;

  try {
    window._hsq = window._hsq || []; // eslint-disable-line no-underscore-dangle -- Hubspot naming rules
    const { _hsq } = window; // eslint-disable-line @typescript-eslint/naming-convention -- Hubspot naming rules
    _hsq.push(['identify', { email: trimmed }]);
  } catch {
    // HubSpot is best effort.
  }
};

export const restoreAnalyticsIdentityForAuthenticatedUser = (
  user: User,
): void => {
  const key = identityKey(user.email);
  if (!key || identifiedEmailsThisDocument.has(key)) return;

  identify(user);
};

export const releaseFullStoryIdentityForLogout = (): void => {
  identifiedEmailsThisDocument.clear();

  try {
    if (getFullstoryOrgId()) anonymizeFullStory();
  } catch {
    // FullStory is best effort.
  }
};

export const track = (
  eventName: string,
  data?: Record<string, unknown>,
): void => {
  if (isPreAuthenticationEvent(eventName)) return;
  if (eventName.startsWith('Viewed ')) {
    // MEN-5426: hand-written page views also carry the URL's query parameters.
    momentsAnalytics.track(eventName, data ?? {}, {
      queryParams: Object.fromEntries(
        new URLSearchParams(window.location.search),
      ),
    });
    return;
  }
  momentsAnalytics.track(eventName, data ?? {});
};

export const trackView = (
  path: string,
  queryParams?: Record<string, string>,
): void => {
  try {
    window._hsq = window._hsq || []; // eslint-disable-line no-underscore-dangle -- Hubspot naming rules
    const { _hsq } = window; // eslint-disable-line @typescript-eslint/naming-convention -- Hubspot naming rules
    _hsq.push(['setPath', path]);
    _hsq.push(['trackPageView']);
  } catch {
    // HubSpot is best effort.
  }

  momentsAnalytics.track(`Viewed ${path}`, {}, { queryParams });
};
